/**
 * Backend API client for vendor research.
 *
 * Provides typed API calls to the NestJS backend.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

/**
 * Custom error class for API errors.
 */
export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public errorCode: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Research item (analyzed vendor).
 */
export interface Research {
  id: string;
  domain: string;
  name?: string;
  description?: string;
  url: string;
  status: "fresh" | "stale" | "processing" | "error";
  pages_collected: number;
  gemini_file_id: string | null;
  metadata_tags: string[];
  last_scanned: string;
  created_at: string;
  updated_at: string;
}

/**
 * Response from analyze endpoint.
 */
export interface AnalyzeResponse {
  success: boolean;
  cached: boolean;
  id?: string;
  domain: string;
  name?: string;
  gemini_file_id?: string;  // Optional - may be undefined on failure
  pages_collected: number;
  message: string;
  error?: string;
  trace_id?: string;
}

/**
 * Response from query endpoint.
 */
export interface QueryResponse {
  success: boolean;
  domain: string;
  response: string | null;  // Nullable on failure
  gemini_file_id: string;
  model?: string;
  error?: string;
}

/**
 * Available LLM models for vendor analysis.
 */
export const LLM_MODELS = [
  { id: "claude-opus-4-7", name: "Claude Opus 4.7", provider: "anthropic" },
  { id: "claude-opus-4-6", name: "Claude Opus 4.6", provider: "anthropic" },
  { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6", provider: "anthropic" },
  { id: "gemini-3.1-pro-preview", name: "Gemini 3.1 Pro Preview", provider: "google" },
] as const;

export type LLMModelId = typeof LLM_MODELS[number]["id"];

/**
 * Options for analyze request.
 */
export interface AnalyzeOptions {
  name?: string;
  description?: string;
  tags?: string[];
  force_refresh?: boolean;
  depth?: number;
  max_pages?: number;
  model?: LLMModelId;
}

/**
 * Workspace interface.
 */
export interface Workspace {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  vendor_count: number;
  public_vendor_count: number;
  private_vendor_count: number;
  last_activity_at: string;
  reports_generated: number;
  conversations_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Workspace vendor interface (from workspace vendors endpoint).
 */
export interface WorkspaceVendor {
  vendor_id: string;
  vendor_type: "public" | "private";
  vendor_name: string;
  vendor_domain?: string;
  gemini_file_id: string | null;
  vendor_status?: "fresh" | "stale" | "processing" | "error";
  // Evaluation status for this user
  has_evaluation: boolean;
  evaluation_status?: "pending" | "processing" | "completed" | "failed";
  evaluation_id?: string;
}

/**
 * Combined vendor interface (from vendor library).
 */
export interface CombinedVendor {
  id: string;
  type: "public" | "private";
  name: string;
  description?: string;
  metadata_tags: string[];
  gemini_file_id: string | null;
  status: string;
  created_at: string;
  domain?: string;
  url?: string;
  user_id?: string;
  source_file?: {
    storage_path: string;
    original_filename: string;
    mime_type: string;
    size_bytes: number;
    uploaded_at: string;
  };
}

/**
 * Vendor source attribution for assistant messages.
 */
export interface VendorSource {
  vendor_id: string;
  vendor_name: string;
  relevance_score?: number;
  excerpt: string;
}

/**
 * Message interface.
 */
export interface Message {
  id: string;
  conversation_id: string;
  role: "user" | "assistant";
  content: string;
  vendor_sources?: VendorSource[];
  processing_time_ms?: number;
  model_used?: string;
  trace_id?: string;
  created_at: string | Date;
}

/**
 * Conversation interface.
 */
export interface Conversation {
  id: string;
  workspace_id: string;
  user_id: string;
  title: string;
  status: "active" | "archived";
  message_count: number;
  last_message_preview?: string;
  last_message_at?: string | Date;
  created_at: string | Date;
  updated_at: string | Date;
}

/**
 * Custom prompts for rerunning evaluations with edited instructions.
 */
export interface CustomPrompts {
  system_prompt?: string;
  criterion_prompts?: Partial<Record<string, string>>;
  executive_summary_prompt?: string;
  key_questions_prompt?: string;
}

/**
 * Default prompts response from the API with editable/locked zones.
 */
export interface DefaultPromptsResponse {
  success: boolean;
  prompts: {
    system_prompt: { editable: string; locked: string };
    criterion_prompts: Record<string, {
      id: string;
      name: string;
      subtitle: string;
      editable: string;
      locked: string;
    }>;
    executive_summary_prompt: { editable: string; locked: null };
    key_questions_prompt: { editable: string; locked: null };
  };
}

/**
 * Evaluation score type.
 */
export type EvaluationScore = "yes" | "partially" | "no" | "insufficient";

/**
 * Evaluation status type.
 */
export type EvaluationStatus = "pending" | "processing" | "completed" | "failed";

/**
 * Evaluation sentiment type.
 */
export type EvaluationSentiment = "positive" | "neutral" | "negative";

/**
 * Criterion trade-off.
 */
export interface CriterionTradeoff {
  gain: string;
  giveUp: string;
}

/**
 * Grounding source from Gemini File Search.
 * Contains the source page URL and text snippet used for evaluation.
 */
export interface GroundingSource {
  uri: string;
  title?: string;
  snippet: string;
}

/**
 * Criterion evaluation result.
 */
export interface CriterionEvaluation {
  criterion_id: string;
  criterion_name: string;
  subtitle: string;
  score: EvaluationScore;
  assessment: string;
  tradeoff: CriterionTradeoff;
  questions: string[];
  why_it_matters: string[];
  sources?: GroundingSource[];
}

/**
 * Executive summary.
 */
export interface ExecutiveSummary {
  key_takeaway: string;
  sentiment: EvaluationSentiment;
  paragraphs: string[];
}

/**
 * Vendor evaluation.
 */
export interface VendorEvaluation {
  id: string;
  user_id?: string;  // Owner of the evaluation
  vendor_id: string;
  vendor_name: string;
  vendor_category: string;
  evaluation_date: string | Date;
  executive_summary: ExecutiveSummary;
  criteria: CriterionEvaluation[];
  key_questions: string[];
  procurement_assessment?: string;
  procurement_error?: string;
  status: EvaluationStatus;
  error_message?: string;
  processing_time_ms?: number;
  trace_id?: string;
  writing_style_enhanced?: boolean;
  custom_prompts_used?: boolean;
  custom_prompts?: CustomPrompts;
  created_at: string | Date;
  updated_at: string | Date;
}

/**
 * Narrative rewrite content (mirrors evaluation text fields).
 */
export interface NarrativeRewriteContent {
  executive_summary: {
    key_takeaway: string;
    paragraphs: string[];
  };
  criteria: Array<{
    criterion_id: string;
    assessment: string;
    tradeoff: CriterionTradeoff;
  }>;
  procurement_assessment?: string;
  key_questions: string[];
}

/**
 * Response from the narrative rewrite endpoint.
 */
export interface NarrativeRewriteResponse {
  success: boolean;
  rewrite?: NarrativeRewriteContent;
  error?: string;
  cached?: boolean;
}

/**
 * Get the auth token from localStorage.
 */
function getAuthToken(): string | null {
  return localStorage.getItem("auth_token");
}

/**
 * Fetch wrapper with error handling.
 */
async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit & { requireAuth?: boolean }
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const { requireAuth = false, ...fetchOptions } = options || {};

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers as Record<string, string>),
  };

  // Add Authorization header if auth is required
  if (requireAuth) {
    const token = getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    let errorData: { error?: string; message?: string };
    try {
      errorData = await response.json();
    } catch {
      errorData = {
        error: "unknown_error",
        message: `Request failed with status ${response.status}`,
      };
    }
    throw new ApiError(
      response.status,
      errorData.error || "unknown_error",
      errorData.message || "Unknown error"
    );
  }

  return response.json();
}

/**
 * User role type.
 */
export type UserRole = 'admin' | 'user' | 'disabled';

/**
 * User info returned from auth endpoints.
 */
export interface AuthUser {
  id: string;
  email: string;
  display_name?: string;
  avatar_url?: string;
  role: UserRole;
  can_edit_reports?: boolean;
}

/**
 * Auth response from login/register endpoints.
 */
export interface AuthResponse {
  access_token: string;
  user: AuthUser;
}

/**
 * Register response - either successful auth or waitlisted.
 */
export interface RegisterResponse {
  access_token?: string;
  user?: AuthUser;
  waitlisted?: boolean;
  message?: string;
}

/**
 * Admin user info (for user management).
 */
export interface AdminUser {
  id: string;
  email: string;
  display_name?: string;
  avatar_url?: string;
  role: UserRole;
  status?: 'active' | 'pending';
  can_edit_reports?: boolean;
  invited_by?: string;
  primary_provider: string;
  timezone?: string;
  daily_usage?: { used: number; limit: number };
  created_at: string;
  updated_at: string;
}

/**
 * Email allowlist rule interface.
 */
export interface EmailAllowlistRule {
  id: string;
  type: 'domain' | 'email';
  value: string;
  max_invites: number;
  used_slots: number;
  created_at: string;
  updated_at: string;
}

/**
 * OAuth authorization URL response.
 */
export interface OAuthAuthUrlResponse {
  url: string;
  configured: boolean;
  silent?: boolean;
}

/** @deprecated Use OAuthAuthUrlResponse */
export type DiscordAuthUrlResponse = OAuthAuthUrlResponse;

export type OAuthProviderId = "discord" | "feedforward";

/**
 * Usage stats response from admin endpoint.
 */
export interface UsageStatsResponse {
  success: boolean;
  dailyTotals: Array<{ date: string; count: number }>;
  perUser: Array<{ userId: string; email: string; totalCount: number; todayCount: number }>;
}

/**
 * Admin settings response.
 */
export interface AdminSettingsResponse {
  success: boolean;
  settings: {
    daily_evaluation_limit: number;
  };
}

/**
 * API client for vendor research.
 */
export const api = {
  /**
   * Register a new user account.
   *
   * @param email - User email address
   * @param password - User password (minimum 8 characters)
   * @returns Register response with access token or waitlist status
   */
  async register(email: string, password: string): Promise<RegisterResponse> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return fetchApi<RegisterResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, timezone }),
      requireAuth: false,
    });
  },

  /**
   * Login with email and password.
   *
   * @param email - User email address
   * @param password - User password
   * @returns Auth response with access token
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    return fetchApi<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      requireAuth: false,
    });
  },

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    return fetchApi<{ success: boolean; message: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
      requireAuth: false,
    });
  },

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return fetchApi<{ success: boolean; message: string }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
      requireAuth: false,
    });
  },

  /**
   * Get OAuth authorization URL for a provider.
   */
  async getOAuthAuthUrl(
    provider: OAuthProviderId,
    options?: { silent?: boolean },
  ): Promise<OAuthAuthUrlResponse> {
    const query = options?.silent ? "?silent=true" : "";
    return fetchApi<OAuthAuthUrlResponse>(`/auth/oauth/${provider}${query}`, {
      requireAuth: false,
    });
  },

  /**
   * Exchange OAuth authorization code for JWT token.
   */
  async loginWithOAuth(
    provider: OAuthProviderId,
    code: string,
  ): Promise<AuthResponse> {
    return fetchApi<AuthResponse>(`/auth/oauth/${provider}/callback`, {
      method: "POST",
      body: JSON.stringify({ code }),
      requireAuth: false,
    });
  },

  /** @deprecated Use getOAuthAuthUrl('discord') */
  async getDiscordAuthUrl(): Promise<OAuthAuthUrlResponse> {
    return this.getOAuthAuthUrl("discord");
  },

  /** @deprecated Use loginWithOAuth('discord', code) */
  async loginWithDiscord(code: string): Promise<AuthResponse> {
    return this.loginWithOAuth("discord", code);
  },

  /**
   * List all researches (analyzed vendors).
   */
  async listResearches(): Promise<{ success: boolean; count: number; researches: Research[] }> {
    return fetchApi("/research", { requireAuth: true });
  },

  /**
   * Get a specific research by ID.
   */
  async getResearch(id: string): Promise<{ success: boolean; research: Research }> {
    return fetchApi(`/research/${encodeURIComponent(id)}`, { requireAuth: true });
  },

  /**
   * Analyze a vendor URL.
   *
   * This triggers the full analysis pipeline:
   * - Checks if URL has been analyzed recently (24hr cache)
   * - Discovers and scrapes related pages
   * - Uploads content to Gemini for querying
   *
   * @param url - The vendor URL to analyze (e.g., "https://glean.com")
   * @param options - Optional settings for name, tags, depth, max pages, force refresh
   * @returns Analysis result with gemini_file_id
   */
  async analyze(
    url: string,
    options?: AnalyzeOptions & { signal?: AbortSignal }
  ): Promise<AnalyzeResponse> {
    const { signal, ...body } = options || {};
    return fetchApi<AnalyzeResponse>("/research/analyze", {
      method: "POST",
      body: JSON.stringify({ url, ...body }),
      signal,
      requireAuth: true,
    });
  },

  /**
   * Analyze a vendor URL asynchronously.
   * Returns immediately after creating the vendor record.
   * The crawl continues in the background.
   *
   * @param url - The vendor URL to analyze
   * @param options - Optional settings for name, tags, depth, max pages
   * @returns Immediate result with vendor ID (status will be 'processing')
   */
  async analyzeAsync(
    url: string,
    options?: AnalyzeOptions & { workspace_id?: string }
  ): Promise<AnalyzeResponse> {
    return fetchApi<AnalyzeResponse>("/research/analyze-async", {
      method: "POST",
      body: JSON.stringify({ url, ...options }),
      requireAuth: true,
    });
  },

  /**
   * Query an analyzed vendor.
   *
   * Ask questions about a vendor that has been analyzed.
   * The URL must have been analyzed first.
   *
   * @param url - The vendor URL to query
   * @param query - Natural language question about the vendor
   * @param model - Optional Gemini model to use
   * @returns Query response with answer
   */
  async query(
    url: string,
    query: string,
    options?: { model?: string; signal?: AbortSignal }
  ): Promise<QueryResponse> {
    const { signal, model } = options || {};
    return fetchApi<QueryResponse>("/research/query", {
      method: "POST",
      body: JSON.stringify({ url, query, model }),
      signal,
      requireAuth: true,
    });
  },

  /**
   * Check URL freshness status.
   *
   * @param url - The URL to check
   * @returns Freshness status
   */
  async checkFreshness(
    url: string
  ): Promise<{ url: string; status: string; lastScanned?: string }> {
    return fetchApi(`/vendors/freshness?url=${encodeURIComponent(url)}`, {
      requireAuth: true,
    });
  },

  /**
   * List all workspaces for the authenticated user.
   *
   * @returns List of workspaces
   */
  async listWorkspaces(): Promise<{ success: boolean; count: number; workspaces: Workspace[] }> {
    return fetchApi("/workspaces", { requireAuth: true });
  },

  /**
   * Get vendors for a specific workspace.
   *
   * @param workspaceId - The workspace ID
   * @param signal - Optional AbortSignal to cancel the request
   * @returns List of workspace vendors
   */
  async getWorkspaceVendors(
    workspaceId: string,
    signal?: AbortSignal
  ): Promise<{ success: boolean; count: number; vendors: WorkspaceVendor[] }> {
    return fetchApi(`/workspaces/${encodeURIComponent(workspaceId)}/vendors`, {
      requireAuth: true,
      signal,
    });
  },

  /**
   * List all available vendors (public and private).
   *
   * @param type - Optional filter by type ('public' or 'private')
   * @param signal - Optional AbortSignal to cancel the request
   * @returns List of combined vendors
   */
  async listVendors(type?: "public" | "private", signal?: AbortSignal): Promise<CombinedVendor[]> {
    const query = type ? `?type=${type}` : "";
    return fetchApi(`/vendors${query}`, { requireAuth: true, signal });
  },

  /**
   * Add a vendor to a workspace.
   *
   * @param workspaceId - The workspace ID
   * @param vendorId - The vendor ID
   * @param vendorType - The vendor type ('public' or 'private')
   * @returns Success response with assignment
   */
  async addVendorToWorkspace(
    workspaceId: string,
    vendorId: string,
    vendorType: "public" | "private"
  ): Promise<{ success: boolean; assignment: WorkspaceVendor }> {
    return fetchApi(`/workspaces/${encodeURIComponent(workspaceId)}/vendors`, {
      method: "POST",
      body: JSON.stringify({
        vendor_id: vendorId,
        vendor_type: vendorType,
      }),
      requireAuth: true,
    });
  },

  /**
   * Remove a vendor from a workspace. Admin only.
   */
  async removeVendorFromWorkspace(
    workspaceId: string,
    vendorId: string
  ): Promise<{ success: boolean }> {
    return fetchApi(
      `/workspaces/${encodeURIComponent(workspaceId)}/vendors/${encodeURIComponent(vendorId)}`,
      {
        method: "DELETE",
        requireAuth: true,
      }
    );
  },

  /**
   * Create a new public vendor by analyzing a URL.
   * This will analyze the vendor website and create a public vendor entry.
   *
   * @param url - The vendor website URL
   * @param name - Optional vendor name
   * @param description - Optional vendor description
   * @param tags - Optional metadata tags
   * @returns Analysis result with vendor information
   */
  async createVendorFromUrl(
    url: string,
    options?: { name?: string; description?: string; tags?: string[] }
  ): Promise<AnalyzeResponse> {
    // Normalize URL - ensure it has a protocol
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    return this.analyze(normalizedUrl, {
      name: options?.name,
      description: options?.description,
      tags: options?.tags,
    });
  },

  /**
   * Create a new conversation.
   *
   * @param workspaceId - The workspace ID
   * @param title - Optional conversation title
   * @returns Success response with conversation
   */
  async createConversation(
    workspaceId: string,
    title?: string
  ): Promise<{ success: boolean; conversation: Conversation }> {
    return fetchApi("/conversations", {
      method: "POST",
      body: JSON.stringify({
        workspace_id: workspaceId,
        title,
      }),
      requireAuth: true,
    });
  },

  /**
   * List conversations for the authenticated user.
   *
   * @param workspaceId - Optional workspace ID to filter by
   * @returns List of conversations
   */
  async listConversations(
    workspaceId?: string
  ): Promise<{ success: boolean; count: number; conversations: Conversation[] }> {
    const query = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : "";
    return fetchApi(`/conversations${query}`, { requireAuth: true });
  },

  /**
   * Get a specific conversation by ID.
   *
   * @param id - The conversation ID
   * @returns Conversation details
   */
  async getConversation(id: string): Promise<{ success: boolean; conversation: Conversation }> {
    return fetchApi(`/conversations/${encodeURIComponent(id)}`, { requireAuth: true });
  },

  /**
   * Get messages for a conversation.
   *
   * @param conversationId - The conversation ID
   * @param limit - Optional limit (default: 50)
   * @param offset - Optional offset (default: 0)
   * @returns List of messages
   */
  async getConversationMessages(
    conversationId: string,
    limit?: number,
    offset?: number
  ): Promise<{ success: boolean; count: number; messages: Message[] }> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append("limit", limit.toString());
    if (offset !== undefined) params.append("offset", offset.toString());
    const query = params.toString() ? `?${params.toString()}` : "";
    return fetchApi(`/conversations/${encodeURIComponent(conversationId)}/messages${query}`, {
      requireAuth: true,
    });
  },

  /**
   * Send a message in a conversation.
   *
   * @param conversationId - The conversation ID
   * @param content - The message content
   * @returns Success response with user and assistant messages
   */
  async sendMessage(
    conversationId: string,
    content: string
  ): Promise<{
    success: boolean;
    user_message: Message;
    assistant_message: Message;
  }> {
    return fetchApi(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
      method: "POST",
      body: JSON.stringify({ content }),
      requireAuth: true,
    });
  },

  /**
   * Update a conversation.
   *
   * @param conversationId - The conversation ID
   * @param updates - Update fields (title and/or status)
   * @returns Success response with updated conversation
   */
  async updateConversation(
    conversationId: string,
    updates: { title?: string; status?: "active" | "archived" }
  ): Promise<{ success: boolean; conversation: Conversation }> {
    return fetchApi(`/conversations/${encodeURIComponent(conversationId)}`, {
      method: "PUT",
      body: JSON.stringify(updates),
      requireAuth: true,
    });
  },

  /**
   * Archive a conversation.
   *
   * @param conversationId - The conversation ID
   * @returns Success response with archived conversation
   */
  async archiveConversation(
    conversationId: string
  ): Promise<{ success: boolean; conversation: Conversation }> {
    return fetchApi(`/conversations/${encodeURIComponent(conversationId)}/archive`, {
      method: "PUT",
      requireAuth: true,
    });
  },

  // ==================== Evaluations ====================

  /**
   * Start a new vendor evaluation.
   *
   * @param vendorId - The vendor ID to evaluate
   * @returns Success response with evaluation (initially in 'pending' status)
   */
  async startEvaluation(
    vendorId: string,
    options?: { model?: LLMModelId; custom_prompts?: CustomPrompts }
  ): Promise<{ success: boolean; evaluation: VendorEvaluation }> {
    return fetchApi("/evaluations", {
      method: "POST",
      body: JSON.stringify({
        vendor_id: vendorId,
        ...(options?.model && { model: options.model }),
        ...(options?.custom_prompts && { custom_prompts: options.custom_prompts }),
      }),
      requireAuth: true,
    });
  },

  /**
   * Get default evaluation prompts with editable/locked zones.
   */
  async getDefaultPrompts(): Promise<DefaultPromptsResponse> {
    return fetchApi("/evaluations/default-prompts", { requireAuth: true });
  },

  /**
   * List all evaluations.
   *
   * @returns List of evaluations
   */
  async listEvaluations(): Promise<{
    success: boolean;
    count: number;
    evaluations: VendorEvaluation[];
  }> {
    return fetchApi("/evaluations", { requireAuth: true });
  },

  /**
   * Get an evaluation by ID.
   *
   * @param evaluationId - The evaluation ID
   * @returns Evaluation details
   */
  async getEvaluation(
    evaluationId: string
  ): Promise<{ success: boolean; evaluation: VendorEvaluation }> {
    return fetchApi(`/evaluations/${encodeURIComponent(evaluationId)}`, {
      requireAuth: true,
    });
  },

  /**
   * Get evaluation status.
   *
   * @param evaluationId - The evaluation ID
   * @returns Evaluation status
   */
  async getEvaluationStatus(
    evaluationId: string
  ): Promise<{ success: boolean; id: string; status: EvaluationStatus; error_message?: string }> {
    return fetchApi(`/evaluations/${encodeURIComponent(evaluationId)}/status`, {
      requireAuth: true,
    });
  },

  /**
   * Get the latest evaluation for a vendor.
   *
   * @param vendorId - The vendor ID
   * @returns Latest evaluation or null if none exists
   */
  async getVendorEvaluation(
    vendorId: string
  ): Promise<{ success: boolean; evaluation: VendorEvaluation | null }> {
    return fetchApi(`/evaluations/vendor/${encodeURIComponent(vendorId)}`, {
      requireAuth: true,
    });
  },

  /**
   * Get all evaluations for a vendor.
   *
   * @param vendorId - The vendor ID
   * @returns List of evaluations for the vendor
   */
  async getVendorEvaluations(
    vendorId: string
  ): Promise<{ success: boolean; count: number; evaluations: VendorEvaluation[] }> {
    return fetchApi(`/evaluations/vendor/${encodeURIComponent(vendorId)}/all`, {
      requireAuth: true,
    });
  },

  /**
   * Update a completed evaluation (for whitelisted editors and admins).
   */
  async updateEvaluation(
    evaluationId: string,
    updates: {
      executive_summary?: { key_takeaway?: string; sentiment?: string; paragraphs?: string[] };
      criteria?: { criterion_id: string; score?: string; assessment?: string; tradeoff?: { gain?: string; giveUp?: string } }[];
      key_questions?: string[];
      procurement_assessment?: string;
    }
  ): Promise<{ success: boolean; evaluation: VendorEvaluation }> {
    return fetchApi(`/evaluations/${encodeURIComponent(evaluationId)}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
      requireAuth: true,
    });
  },

  /**
   * Poll evaluation status until completed or failed.
   *
   * @param evaluationId - The evaluation ID
   * @param options - Polling options
   * @returns Completed evaluation
   */
  async pollEvaluationUntilComplete(
    evaluationId: string,
    options?: { interval?: number; maxAttempts?: number; onProgress?: (status: EvaluationStatus) => void }
  ): Promise<VendorEvaluation> {
    const { interval = 2000, maxAttempts = 60, onProgress } = options || {};
    let attempts = 0;

    while (attempts < maxAttempts) {
      const { status, error_message } = await this.getEvaluationStatus(evaluationId);

      if (onProgress) {
        onProgress(status);
      }

      if (status === "completed") {
        const { evaluation } = await this.getEvaluation(evaluationId);
        return evaluation;
      }

      if (status === "failed") {
        throw new ApiError(500, "evaluation_failed", error_message || "Evaluation failed");
      }

      await new Promise((resolve) => setTimeout(resolve, interval));
      attempts++;
    }

    throw new ApiError(408, "timeout", "Evaluation timed out");
  },

  // ==================== Voice Rewrites ====================

  /**
   * Get or generate a narrative voice rewrite for an evaluation.
   * Returns cached version if available, otherwise triggers LLM rewrite.
   */
  async getNarrativeRewrite(
    evaluationId: string
  ): Promise<NarrativeRewriteResponse> {
    return fetchApi(`/evaluations/${encodeURIComponent(evaluationId)}/narrative-rewrite`, {
      method: "POST",
      requireAuth: true,
    });
  },

  /**
   * Get or generate a consultant voice rewrite for an evaluation.
   * Returns cached version if available, otherwise triggers LLM rewrite.
   */
  async getConsultantRewrite(
    evaluationId: string
  ): Promise<NarrativeRewriteResponse> {
    return fetchApi(`/evaluations/${encodeURIComponent(evaluationId)}/consultant-rewrite`, {
      method: "POST",
      requireAuth: true,
    });
  },

  // ==================== Infographics ====================

  /**
   * Generate an infographic for an evaluation.
   *
   * @param evaluationId - The evaluation ID
   * @param options - Provider and regenerate options
   * @returns Infographic response with image data
   */
  async generateInfographic(
    evaluationId: string,
    options: { provider?: "gemini" | "dalle"; regenerate?: boolean } = {}
  ): Promise<InfographicResponse> {
    return fetchApi(`/evaluations/${encodeURIComponent(evaluationId)}/infographic`, {
      method: "POST",
      body: JSON.stringify({
        provider: options.provider,
        regenerate: options.regenerate,
      }),
      requireAuth: true,
    });
  },

  /**
   * Get available image generation providers.
   *
   * @returns List of available providers
   */
  async getInfographicProviders(): Promise<InfographicProvidersResponse> {
    return fetchApi("/evaluations/infographic/providers", {
      requireAuth: true,
    });
  },

  // ==================== Admin: User Management ====================

  /**
   * List all users (admin only).
   *
   * @returns List of users
   */
  async listUsers(): Promise<{ success: boolean; count: number; users: AdminUser[] }> {
    return fetchApi("/users", { requireAuth: true });
  },

  /**
   * Update a user's role (admin only).
   *
   * @param userId - The user ID
   * @param role - The new role ('admin' or 'user')
   * @returns Updated user
   */
  async updateUserRole(
    userId: string,
    role: UserRole
  ): Promise<{ success: boolean; user: AdminUser }> {
    return fetchApi(`/users/${encodeURIComponent(userId)}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
      requireAuth: true,
    });
  },

  /**
   * Activate a pending (waitlisted) user (admin only).
   *
   * @param userId - The user ID to activate
   * @returns Updated user
   */
  async activateUser(
    userId: string
  ): Promise<{ success: boolean; user: AdminUser }> {
    return fetchApi(`/users/${encodeURIComponent(userId)}/activate`, {
      method: "PATCH",
      requireAuth: true,
    });
  },

  /**
   * Toggle a user's can_edit_reports flag (admin only).
   */
  async updateCanEditReports(
    userId: string,
    canEditReports: boolean
  ): Promise<{ success: boolean; user: AdminUser }> {
    return fetchApi(`/users/${encodeURIComponent(userId)}/can-edit-reports`, {
      method: "PATCH",
      body: JSON.stringify({ can_edit_reports: canEditReports }),
      requireAuth: true,
    });
  },

  // ==================== Email Allowlist (Admin) ====================

  async listEmailAllowlist(): Promise<{
    success: boolean;
    count: number;
    rules: EmailAllowlistRule[];
  }> {
    return fetchApi("/email-allowlist", { requireAuth: true });
  },

  async createEmailAllowlistRule(
    type: 'domain' | 'email',
    value: string,
    maxInvites: number
  ): Promise<{ success: boolean; rule: EmailAllowlistRule }> {
    return fetchApi("/email-allowlist", {
      method: "POST",
      body: JSON.stringify({ type, value, max_invites: maxInvites }),
      requireAuth: true,
    });
  },

  async updateEmailAllowlistRule(
    id: string,
    updates: { value?: string; max_invites?: number }
  ): Promise<{ success: boolean; rule: EmailAllowlistRule }> {
    return fetchApi(`/email-allowlist/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(updates),
      requireAuth: true,
    });
  },

  async deleteEmailAllowlistRule(
    id: string
  ): Promise<{ success: boolean }> {
    return fetchApi(`/email-allowlist/${encodeURIComponent(id)}`, {
      method: "DELETE",
      requireAuth: true,
    });
  },

  /**
 * Submit user feedback.
 */
  async submitFeedback(data: {
    type: 'technical_defect' | 'general_feedback' | 'other';
    message: string;
    page_url?: string;
  }): Promise<{ success: boolean }> {
    return fetchApi("/feedback", {
      method: "POST",
      body: JSON.stringify(data),
      requireAuth: true,
    });
  },

  // ==================== Admin Stats & Settings ====================

  /**
   * Get evaluation usage statistics for the admin dashboard.
   */
  async getUsageStats(days: number = 30): Promise<UsageStatsResponse> {
    return fetchApi(`/admin/stats/usage?days=${days}`, { requireAuth: true });
  },

  /**
   * Get current admin settings (rate limits).
   */
  async getAdminSettings(): Promise<AdminSettingsResponse> {
    return fetchApi("/admin/settings", { requireAuth: true });
  },

  /**
   * Update admin settings (rate limits).
   */
  async updateAdminSettings(settings: { daily_evaluation_limit: number }): Promise<AdminSettingsResponse> {
    return fetchApi("/admin/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
      requireAuth: true,
    });
  },
};

/**
 * Image generation provider type.
 */
export type ImageGenerationProvider = "gemini" | "dalle";

/**
 * Infographic generation response.
 */
export interface InfographicResponse {
  success: boolean;
  image_url?: string;
  image_base64?: string;
  provider: ImageGenerationProvider;
  error?: string;
  cached?: boolean;
}

/**
 * Infographic providers response.
 */
export interface InfographicProvidersResponse {
  success: boolean;
  providers: ImageGenerationProvider[];
  default: ImageGenerationProvider | null;
}

// ==================== Vendor Call Notes ====================

/**
 * Vendor call notes interface.
 */
export interface VendorCallNotes {
  id: string;
  user_id: string;
  vendor_domain: string;
  user_notes: string;
  ai_notes: Array<{
    id: string;
    timestamp: string;
    category: string;
    content: string;
  }>;
  questions_status: Array<{
    question_id: string;
    answered: boolean;
    answered_by_ai?: boolean;
    answer?: string;
  }>;
  generated_questions: Array<{
    id: string;
    category: string;
    question: string;
    context?: string;
    criterion_id?: string;
    aiSuggested?: boolean;
  }>;
  created_at: string;
  updated_at: string;
}

/**
 * Update vendor call notes input.
 */
export interface UpdateVendorCallNotesInput {
  user_notes?: string;
  ai_notes?: Array<{
    id: string;
    timestamp: string;
    category: string;
    content: string;
  }>;
  questions_status?: Array<{
    question_id: string;
    answered: boolean;
    answered_by_ai?: boolean;
    answer?: string;
  }>;
  generated_questions?: Array<{
    id: string;
    category: string;
    question: string;
    context?: string;
    criterion_id?: string;
    aiSuggested?: boolean;
  }>;
}

/**
 * Generated vendor call question (from AI).
 */
export interface GeneratedCallQuestion {
  id: string;
  category: string;
  question: string;
  context?: string;
  criterion_id?: string;
  aiSuggested: true;
}

/**
 * Vendor call notes API methods.
 */
export const vendorCallNotesApi = {
  /**
   * Generate AI vendor call questions based on evaluation and user context.
   */
  async generateCallQuestions(params: {
    evaluation_id: string;
    budget: string;
    priority: string;
    familiarity: string;
  }): Promise<{ success: boolean; questions: GeneratedCallQuestion[]; error?: string }> {
    return fetchApi("/vendor-call-notes/generate-questions", {
      method: "POST",
      body: JSON.stringify(params),
      requireAuth: true,
    });
  },

  /**
   * Get vendor call notes for a specific vendor.
   *
   * @param vendorDomain - The vendor domain
   * @returns Notes or null if none exist
   */
  async getVendorCallNotes(
    vendorDomain: string
  ): Promise<{ success: boolean; notes: VendorCallNotes | null }> {
    return fetchApi(`/vendor-call-notes/${encodeURIComponent(vendorDomain)}`, {
      requireAuth: true,
    });
  },

  /**
   * Update vendor call notes for a specific vendor.
   * This endpoint supports partial updates (only provided fields are updated).
   *
   * @param vendorDomain - The vendor domain
   * @param updates - The fields to update
   * @returns Updated notes
   */
  async updateVendorCallNotes(
    vendorDomain: string,
    updates: UpdateVendorCallNotesInput
  ): Promise<{ success: boolean; notes: VendorCallNotes }> {
    return fetchApi(`/vendor-call-notes/${encodeURIComponent(vendorDomain)}`, {
      method: "PUT",
      body: JSON.stringify(updates),
      requireAuth: true,
    });
  },

  /**
   * Delete vendor call notes for a specific vendor.
   *
   * @param vendorDomain - The vendor domain
   * @returns Success response
   */
  async deleteVendorCallNotes(
    vendorDomain: string
  ): Promise<{ success: boolean }> {
    return fetchApi(`/vendor-call-notes/${encodeURIComponent(vendorDomain)}`, {
      method: "DELETE",
      requireAuth: true,
    });
  },

  /**
   * List all vendor call notes for the user.
   *
   * @returns List of notes
   */
  async listVendorCallNotes(): Promise<{
    success: boolean;
    count: number;
    notes: VendorCallNotes[];
  }> {
    return fetchApi("/vendor-call-notes", { requireAuth: true });
  },


};

export default api;
